import type { ReactNode } from "react";
import { formatTargetLabel } from "./TargetFields";
import { formatKabulDisplay } from "./ScheduleFields";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";
import {
  advertiserStatusLabelFa,
  FILTER_ALL,
  FILTER_PAUSED,
  FILTER_QUEUE,
  isSponsoredWindowExpired,
  sponsoredCampaignTitleMeta,
  sponsoredCreativeFilename,
  sponsoredCreativeState,
  sponsoredCtaLabelFa,
  sponsoredEligibility,
  sponsoredStateLabelFa,
  type SponsoredCampaignRow,
  type SponsoredSortKey,
} from "../lib/sponsored-ops";
import { cmsStrings } from "../terminology";

type FilterBarProps = {
  listFilter: string;
  onListFilterChange: (value: string) => void;
  queueCount: number;
  query: string;
  onQueryChange: (value: string) => void;
  sort: SponsoredSortKey;
  onSortChange: (value: SponsoredSortKey) => void;
};

export function SponsoredFilterBar({
  listFilter,
  onListFilterChange,
  queueCount,
  query,
  onQueryChange,
  sort,
  onSortChange,
}: FilterBarProps) {
  return (
    <FilterToolbar
      className="list-filter-bar announcements-filter-bar"
      aria-label={cmsStrings.inventory.filterStatusAria}
    >
      <div className="filter-toolbar__chips announcements-filter-bar__chips" role="group" aria-label={cmsStrings.inventory.filterStatusAria}>
        <button
          type="button"
          className={`filter-chip${listFilter === FILTER_ALL ? " filter-chip--active" : ""}`}
          onClick={() => onListFilterChange(FILTER_ALL)}
          aria-pressed={listFilter === FILTER_ALL}
        >
          {cmsStrings.inventory.filterAll}
        </button>
        <button
          type="button"
          className={`filter-chip${listFilter === FILTER_QUEUE ? " filter-chip--active" : ""}`}
          onClick={() => onListFilterChange(FILTER_QUEUE)}
          aria-pressed={listFilter === FILTER_QUEUE}
        >
          {cmsStrings.inventory.filterQueue}
          {queueCount > 0 && (
            <>
              {" "}
              (
              <LtrIsolate mono>{queueCount}</LtrIsolate>
              )
            </>
          )}
        </button>
        <button
          type="button"
          className={`filter-chip${listFilter === FILTER_PAUSED ? " filter-chip--active" : ""}`}
          onClick={() => onListFilterChange(FILTER_PAUSED)}
          aria-pressed={listFilter === FILTER_PAUSED}
        >
          {cmsStrings.inventory.filterPaused}
        </button>
      </div>
      <label className="announcements-filter-bar__field">
        <span className="visually-hidden">{cmsStrings.sponsoredUi.searchHidden}</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={cmsStrings.sponsoredUi.searchPlaceholder}
          aria-label={cmsStrings.sponsoredUi.searchAria}
        />
      </label>
      <label className="announcements-filter-bar__field">
        <span>{cmsStrings.inventory.sortLabel}</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as SponsoredSortKey)}
          aria-label={cmsStrings.sponsoredUi.sortAria}
        >
          <option value="updated">{cmsStrings.inventory.sortUpdated}</option>
          <option value="title">{cmsStrings.inventory.sortCampaignName}</option>
          <option value="advertiser">{cmsStrings.inventory.sortAdvertiser}</option>
          <option value="priority">{cmsStrings.inventory.sortPriority}</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

type InspectProps = {
  item: SponsoredCampaignRow;
  sponsoredAllowed: boolean;
};

export function SponsoredInspectSummary({ item, sponsoredAllowed }: InspectProps) {
  const eligibility = sponsoredEligibility(item, { sponsoredAllowed });
  const target = formatTargetLabel(item);
  const ui = cmsStrings.sponsoredUi;
  const composer = cmsStrings.sponsoredComposer;
  const chrome = cmsStrings.composerChrome;
  const stateLabel = sponsoredStateLabelFa(item.state);
  const expired = isSponsoredWindowExpired(item.endsAt);
  const showEligibilityDetail =
    eligibility.detail != null && eligibility.detail !== stateLabel;
  const hasAudit =
    Boolean(item.updatedAt) ||
    Boolean(item.submittedAt) ||
    Boolean(item.rejectionReason) ||
    (item.state === "paused" && Boolean(item.pausedFromState));
  const hasImage = Boolean(item.imagePath?.trim());

  return (
    <section
      className="content-inspect content-inspect--sponsored"
      aria-labelledby="sponsored-inspect-title"
    >
      <header className="content-inspect__hero">
        <div className="content-inspect__hero-row">
          <h2 id="sponsored-inspect-title" className="content-inspect__name">
            {item.name}
          </h2>
          <div className="content-inspect__badges" aria-label={ui.statusAria}>
            <span className={`state-badge state-badge--${item.state}`}>{stateLabel}</span>
            {expired ? (
              <span className="state-badge state-badge--expired">{ui.factExpiredWindow}</span>
            ) : null}
          </div>
        </div>
        <p className="content-inspect__id">
          {cmsStrings.inventory.factId}{" "}
          <LtrIsolate mono>{item.id}</LtrIsolate>
        </p>
        <p className="content-inspect__meta-row">
          <span className="content-inspect__meta-item">
            <span className="content-inspect__inline-label">{ui.factAdvertiser}</span>
            {item.advertiserName}
            <span className="content-inspect__detail">
              ({advertiserStatusLabelFa(item.advertiserStatus)})
            </span>
          </span>
          <span className="content-inspect__meta-sep" aria-hidden="true">
            ·
          </span>
          <span className="content-inspect__meta-item">
            <span className="sponsored-inspect-chip">{item.sponsorshipLabel}</span>
          </span>
        </p>
      </header>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">{ui.inspectSectionGuestCopy}</h3>
        <p className="content-inspect__headline">{item.headline}</p>
        <p className="content-inspect__body">{item.description}</p>
        {item.offerText?.trim() ? (
          <p className="content-inspect__inline-fact">
            <span className="content-inspect__inline-label">{ui.factOffer}</span>
            {item.offerText}
          </p>
        ) : null}
        {hasImage ? (
          <figure className="content-inspect__creative-preview">
            <img
              src={item.imagePath}
              alt={item.imageAlt || item.headline}
              className="content-inspect__creative-thumb"
            />
            <figcaption className="content-inspect__creative-caption">
              {item.imageAlt?.trim() ? (
                <>
                  <span className="content-inspect__inline-label">{composer.imageAltLabel}</span>
                  {item.imageAlt}
                </>
              ) : (
                <span className="content-inspect__detail">{sponsoredCreativeFilename(item.imagePath)}</span>
              )}
            </figcaption>
          </figure>
        ) : null}
      </div>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">{chrome.sectionCtaImage}</h3>
        <dl className="content-inspect__facts content-inspect__facts--stacked">
          <div>
            <dt>{ui.factCta}</dt>
            <dd>{sponsoredCtaLabelFa(item.ctaType, item.ctaLabel)}</dd>
          </div>
          {item.ctaHref ? (
            <div className="content-inspect__fact--wide">
              <dt>{ui.factCtaUrl}</dt>
              <dd>
                <code className="content-inspect__mono" dir="ltr" title={item.ctaHref}>
                  {item.ctaHref}
                </code>
              </dd>
            </div>
          ) : null}
          <div>
            <dt>{ui.factCreativePath}</dt>
            <dd>
              {hasImage ? (
                <>
                  <span>{sponsoredCreativeFilename(item.imagePath)}</span>
                  <code
                    className="content-inspect__mono content-inspect__mono--subtle"
                    dir="ltr"
                    title={item.imagePath}
                  >
                    {item.imagePath}
                  </code>
                </>
              ) : (
                sponsoredCreativeState(item.imagePath).label
              )}
            </dd>
          </div>
        </dl>
      </div>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">{ui.inspectSectionPlacement}</h3>
        <dl className="content-inspect__facts content-inspect__facts--pair">
          <div>
            <dt>{cmsStrings.inventory.factTargeting}</dt>
            <dd>
              {target}
              {item.siteCode ? (
                <>
                  {" "}
                  <bdi dir="ltr" className="ltr-isolate latin-digits">
                    ({item.siteCode})
                  </bdi>
                </>
              ) : null}
            </dd>
          </div>
          <div>
            <dt>{cmsStrings.inventory.factSchedule}</dt>
            <dd>
              <LtrIsolate mono>
                {formatKabulDisplay(item.startsAt)} – {formatKabulDisplay(item.endsAt)}
              </LtrIsolate>
            </dd>
          </div>
          <div>
            {/* Inspect placement: اولویت */}
            <dt>{cmsStrings.inventory.factPriority}</dt>
            <dd>
              <LtrIsolate mono>{item.priority}</LtrIsolate>
            </dd>
          </div>
          <div>
            <dt>{ui.factEligibility}</dt>
            <dd>
              {eligibility.label}
              {showEligibilityDetail ? (
                <span className="content-inspect__detail"> — {eligibility.detail}</span>
              ) : null}
            </dd>
          </div>
        </dl>
      </div>

      {hasAudit ? (
        <div className="content-inspect__block content-inspect__block--audit">
          <h3 className="content-inspect__block-title">{ui.inspectSectionAudit}</h3>
          <dl className="content-inspect__facts content-inspect__facts--pair">
            {item.updatedAt ? (
              <div>
                <dt>{cmsStrings.inventory.factUpdated}</dt>
                <dd>
                  <LtrIsolate mono>{formatKabulDisplay(item.updatedAt)}</LtrIsolate>
                </dd>
              </div>
            ) : null}
            {item.submittedAt ? (
              <div>
                <dt>{ui.factSubmitted}</dt>
                <dd>
                  <LtrIsolate mono>{formatKabulDisplay(item.submittedAt)}</LtrIsolate>
                </dd>
              </div>
            ) : null}
            {item.rejectionReason ? (
              <div className="content-inspect__fact--wide">
                <dt>{composer.rejectReason}</dt>
                <dd>{item.rejectionReason}</dd>
              </div>
            ) : null}
            {item.state === "paused" && item.pausedFromState ? (
              <div>
                <dt>{ui.factPausedFrom}</dt>
                <dd>{sponsoredStateLabelFa(item.pausedFromState)}</dd>
              </div>
            ) : null}
          </dl>
        </div>
      ) : null}
    </section>
  );
}

type AdvertiserRow = {
  id: string;
  displayName: string;
  internalNotes: string | null;
  status: "active" | "archived";
};

type AdvertiserListProps = {
  rows: AdvertiserRow[];
  canArchive: boolean;
  onArchive: (id: string, displayName: string) => void;
  statusActiveLabel: string;
  statusArchivedLabel: string;
  archiveLabel: string;
  notesPrefix: string;
};

export function SponsoredAdvertiserList({
  rows,
  canArchive,
  onArchive,
  statusActiveLabel,
  statusArchivedLabel,
  archiveLabel,
  notesPrefix,
}: AdvertiserListProps) {
  const ui = cmsStrings.sponsoredUi;

  return (
    <>
      <div
        className="advertiser-roster-wrap inventory-table-wrap"
        role="region"
        aria-label={ui.advertisersTableAria}
      >
        <table className="data-table advertiser-roster-table">
          <thead>
            <tr>
              <th scope="col">{ui.colAdvertiserName}</th>
              <th scope="col">{ui.colStatus}</th>
              <th scope="col">{ui.colAdvertiserNotes}</th>
              {canArchive ? <th scope="col" className="advertiser-roster-table__actions-col" /> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <strong className="advertiser-roster-table__name">{row.displayName}</strong>
                </td>
                <td>
                  <span
                    className={`state-badge state-badge--${row.status === "active" ? "published" : "archived"}`}
                  >
                    {row.status === "active" ? statusActiveLabel : statusArchivedLabel}
                  </span>
                </td>
                <td>
                  {row.internalNotes?.trim() ? (
                    <span className="advertiser-roster-table__notes" title={row.internalNotes}>
                      {notesPrefix} {row.internalNotes}
                    </span>
                  ) : (
                    <span className="content-inspect__detail">—</span>
                  )}
                </td>
                {canArchive ? (
                  <td className="advertiser-roster-table__actions">
                    {row.status === "active" ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onArchive(row.id, row.displayName)}
                      >
                        {archiveLabel}
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="advertiser-roster-cards" aria-label={ui.advertisersCardsAria}>
        {rows.map((row) => (
          <li key={row.id} className="advertiser-roster-card">
            <div className="advertiser-roster-card__body">
              <div className="advertiser-roster-card__title-row">
                <strong>{row.displayName}</strong>
                <span
                  className={`state-badge state-badge--${row.status === "active" ? "published" : "archived"}`}
                >
                  {row.status === "active" ? statusActiveLabel : statusArchivedLabel}
                </span>
              </div>
              {row.internalNotes?.trim() ? (
                <p className="advertiser-roster-card__notes">
                  {notesPrefix} {row.internalNotes}
                </p>
              ) : null}
            </div>
            {canArchive && row.status === "active" ? (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => onArchive(row.id, row.displayName)}
              >
                {archiveLabel}
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </>
  );
}

type ListProps = {
  rows: SponsoredCampaignRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  renderActions: (row: SponsoredCampaignRow) => ReactNode;
};

export function SponsoredInventoryList({
  rows,
  selectedId,
  onSelect,
  renderActions,
}: ListProps) {
  const ui = cmsStrings.sponsoredUi;

  return (
    <>
      <div className="sites-inventory-table-wrap inventory-table-wrap" role="region" aria-label={ui.tableAria}>
        <table className="data-table content-inventory-table inventory-table sponsored-inventory-table">
          <thead>
            <tr>
              <th scope="col">{cmsStrings.inventory.sortCampaignName}</th>
              <th scope="col">{ui.colStatus}</th>
              <th scope="col">{ui.colTarget}</th>
              <th scope="col">{cmsStrings.inventory.factSchedule}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const expired = isSponsoredWindowExpired(row.endsAt);
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                      aria-label={`${cmsStrings.inventory.details}: ${row.name}`}
                    >
                      {row.name}
                    </button>
                    <div className="sites-inventory__muted">{sponsoredCampaignTitleMeta(row)}</div>
                  </td>
                  <td>
                    <span className={`state-badge state-badge--${row.state}`}>
                      {sponsoredStateLabelFa(row.state)}
                    </span>
                    {expired ? (
                      <div className="sites-inventory__muted">
                        <span className="state-badge state-badge--expired">{ui.factExpiredWindow}</span>
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={formatTargetLabel(row)}>
                      {formatTargetLabel(row)}
                    </div>
                    {row.siteCode && (
                      <div className="sites-inventory__muted inventory-cell-truncate" title={row.siteCode}>
                        <LtrIsolate mono>{row.siteCode}</LtrIsolate>
                      </div>
                    )}
                  </td>
                  <td>
                    <div
                      className="inventory-cell-truncate"
                      title={`${formatKabulDisplay(row.startsAt)} – ${formatKabulDisplay(row.endsAt)}`}
                    >
                      <LtrIsolate mono>
                        {formatKabulDisplay(row.startsAt)} – {formatKabulDisplay(row.endsAt)}
                      </LtrIsolate>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul className="sites-inventory-cards content-inventory-cards inventory-cards" aria-label={ui.cardsAria}>
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const expired = isSponsoredWindowExpired(row.endsAt);
          return (
            <li
              key={row.id}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
                aria-label={`${cmsStrings.inventory.details}: ${row.name}`}
              >
                <span className="sites-inventory-card__title">{row.name}</span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge state-badge--${row.state}`}>
                    {sponsoredStateLabelFa(row.state)}
                  </span>
                  {expired ? (
                    <span className="state-badge state-badge--expired">{ui.factExpiredWindow}</span>
                  ) : null}
                </span>
                <span className="sites-inventory__muted">{sponsoredCampaignTitleMeta(row)}</span>
                <span className="sites-inventory__muted">
                  {ui.colTarget}: {formatTargetLabel(row)}
                </span>
                <span className="sites-inventory__muted">
                  {cmsStrings.inventory.factSchedule}:{" "}
                  <LtrIsolate mono>
                    {formatKabulDisplay(row.startsAt)} – {formatKabulDisplay(row.endsAt)}
                  </LtrIsolate>
                </span>
              </button>
              <div className="sites-inventory-card__actions">{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
